import { ChevronRightSharp } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { taxesForm } from "../../formSchemas/taxes.form";
import { ITaxes } from "../../interfaces/ITaxes";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import { ICallAPIFunction } from "../../yoello-lib/modules/API2/types";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import EColourPanel from "../../yoello-lib/modules/dynamicForms/interfaces/EColourPanel";
import useTask from "../../hooks/useTask";
import "../../scss/taxes.scss";
import ListLoading from "../util/Loading";
import { IDialogRouteChildren } from "../dialogRoutes/DialogRoute";
import { useChangedDialog } from "../../hooks/useChangedDialog";

interface ITaxesFormData {
  tax_percentage: number;
  enabled: Boolean;
  id?: string;
}
interface ITaxesFormDialog extends IDialogRouteChildren {
  callTaxesAPI: ICallAPIFunction<ITaxes>;
  venueId: string;
  formData?: ITaxesFormData;
  hasPermission?: boolean;
  onFailedInteraction?: Function;
}

export function TaxesFormDialog({
  onClose,
  callTaxesAPI,
  venueId,
  formData = { tax_percentage: 0, enabled: true },
  id,
  isNewItem,
  hasPermission,
  onFailedInteraction,
  onDialogClose
}: ITaxesFormDialog) {

  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { sendSnackbarMessage } = useSnackbarMessages()!;
  const [data, setData] = useState(formData);
  const { increaseDoneTasks } = useTask();
  const { hasFormChanged } = useChangedDialog();

  useEffect(() => {
    //@ts-ignore
    if (!isNewItem && !formData?.tax_band_name) {
      setIsLoading(true);
      callTaxesAPI(HTTPMethods.GET, { query: id }).then((res) => {
        if (res.isSuccessful) {
          setData(res.data!);
          setIsLoading(false);
        }
      });
    }
    // eslint-disable-next-line
  }, []);

  async function onSubmit(data: Partial<ITaxes>) {
    if (!isNewItem) {
      setIsLoading(true);
      const response = await callTaxesAPI(HTTPMethods.PUT, {
        query: formData?.id ?? id,
        body: {
          enabled: data.enabled,
          venue_id: venueId,
        },
      });
      if (response.isSuccessful) {
        sendSnackbarMessage(t("snackbar.TaxUpdateSuccess"), "success");
        if (onDialogClose) {
          onDialogClose();
        } else {
          onClose();
        }
      } else {
        sendSnackbarMessage(t("snackbar.TaxUpdateFail"), "error");
      }
    } else {
      setIsLoading(true);
      const response = await callTaxesAPI(HTTPMethods.POST, {
        body: {
          ...data,
          venue_id: venueId,
        },
      });

      if (response.isSuccessful) {
        increaseDoneTasks();
        sendSnackbarMessage(t("snackbar.TaxCreateSuccess"), "success");
        if (onDialogClose) {
          onDialogClose();
        } else {
          onClose();
        }
      } else {
        sendSnackbarMessage(t("snackbar.TaxCreateFail"), "error");
      }
      setIsLoading(false);
    }
  }
  return isLoading ? (
    <ListLoading />
  ) : (
    <div className="dialog">
      <DynamicForm
        useFormOptions={{
          mode: "onChange",
        }}
        onHandleSubmit={onSubmit}
        formData={data}
        formRows={taxesForm(t, !isNewItem)}
        formKeyName={"Taxes"}
        isDialog={true}
        hasDataChanged={hasFormChanged}
        disableAll={!hasPermission}
        disableAllInteraction={onFailedInteraction}
        formButtons={{
          leftButton: {
            type: "button",
            label: t("forms.buttons.cancel"),
            color: EColourPanel.DEFAULT,
            fullWidth: false,
            onClick: onClose,
          },
          rightButton: {
            endIcon: <ChevronRightSharp />,
            type: "submit",
            label: t("forms.buttons.submit"),
            color: EColourPanel.PRIMARY,
            fullWidth: false,
            disabled: isLoading || !hasPermission,
          },
        }}
      />
    </div>
  );
}
